import { Route, Routes, useParams } from 'react-router';
import { Trans, useLingui } from '@lingui/react/macro';

import { GenericError } from '@/components/GenericError/GenericError';
import IllustrationError from '@/components/IllustrationError';
import { PageLayoutCenter } from '@/components/PageLayout';
import Badge from '@/design_system/Badge';
import Stack from '@/design_system/Stack';
import Tabs from '@/design_system/Tabs';
import { Tab, TabList, TabPanel } from '@/design_system/Tabs/Tabs';
import { useShowToast } from '@/design_system/Toast';
import EditableTitle from '@/design_system/Typography/EditableTitle';
import Paragraph from '@/design_system/Typography/Paragraph';
import { useUpdateWorkflow, useWorkflow } from '@/models/workflow';
import { SettingsBreadcrumbParent } from '@/routes/SettingsBeta/components/Layout/Layout';
import { Loading } from '@/routes/SettingsBeta/components/Loading';
import { NotFoundError } from '@/utils/error/NotFoundError';

import { GeneralSection } from './sections/General/General';
import { ScopeSection } from './sections/Scope/Scope';
import { StepsSection } from './sections/Steps';

export const Workflow = () => {
  const { t } = useLingui();
  const showToast = useShowToast();
  const { id, '*': section } = useParams();

  const {
    data: workflow,
    isLoading: isWorkflowLoading,
    isError: isWorkflowError,
    error: workflowError,
  } = useWorkflow(id);

  const { mutateAsync: updateWorkflow, isPending: isUpdateWorkflowPending } = useUpdateWorkflow();

  if (isWorkflowLoading) {
    return <Loading />;
  }

  if (isWorkflowError || !workflow) {
    let errorMessage = <GenericError />;

    if (workflowError instanceof NotFoundError) {
      errorMessage = (
        <Paragraph>
          <Trans id="settings.entities.entities.entity.not-found">
            Unknown entity or you do not have access to it.
          </Trans>
        </Paragraph>
      );
    }

    return (
      <PageLayoutCenter>
        <IllustrationError />
        {errorMessage}
      </PageLayoutCenter>
    );
  }

  const unnamedLabel = t({
    id: 'settings.workflows.workflow.unnamed',
    message: 'Unnamed workflow',
  });

  const handleSaveWorkflowName = async (name: string) => {
    if (!id) {
      return;
    }

    try {
      await updateWorkflow({
        workflowId: id,
        body: { name },
      });
    } catch {
      showToast({
        text: (
          <Trans id="settings.workflows.workflow.rename.error">
            Could not rename the workflow. Please try again.
          </Trans>
        ),
        type: 'error',
      });
    }
  };

  return (
    <main>
      <Stack
        row
        className="settings-section__record-header"
        gap="0.5rem"
        alignItems="center"
        flexWrap="wrap"
      >
        <SettingsBreadcrumbParent
          to="/settings_beta/workflows/workflows"
          label={<Trans id="settings.workflows.workflow.back-to-workflows">Workflows</Trans>}
        />
        <Stack row gap="1rem" alignItems="center" justifyContent="space-between">
          <EditableTitle
            value={workflow.name ?? unnamedLabel}
            onSave={handleSaveWorkflowName}
            isSaving={isUpdateWorkflowPending}
            titleProps={{ as: 'h1', size: '200', weight: 'medium' }}
            editAriaLabel={t({
              id: 'settings.workflows.workflow.editable-title.edit',
              message: 'Edit workflow name',
            })}
            saveAriaLabel={t({
              id: 'settings.workflows.workflow.editable-title.save',
              message: 'Save workflow name',
            })}
            cancelAriaLabel={t({
              id: 'settings.workflows.workflow.editable-title.cancel',
              message: 'Cancel renaming',
            })}
            inputAriaLabel={t({
              id: 'settings.workflows.workflow.editable-title.input',
              message: 'Workflow name',
            })}
          />
          {workflow.enabled ? (
            <Badge size="x-large" color="primary" hasBorder>
              <Trans id="settings.workflows.workflow.enabled">Enabled</Trans>
            </Badge>
          ) : (
            <Badge size="x-large" color="warning" hasBorder>
              <Trans id="settings.workflows.workflow.disabled">Disabled</Trans>
            </Badge>
          )}
        </Stack>
      </Stack>

      <Tabs selectedKey={section || 'general'}>
        <TabList>
          <Tab id="general" href={`/settings_beta/workflows/workflows/${id}`}>
            <Trans id="settings.workflows.workflow.tab.general">General</Trans>
          </Tab>
          <Tab id="steps" href={`/settings_beta/workflows/workflows/${id}/steps`}>
            <Trans id="settings.workflows.workflow.tab.steps">Steps</Trans>
          </Tab>
          <Tab id="scope" href={`/settings_beta/workflows/workflows/${id}/scope`}>
            <Trans id="settings.workflows.workflow.tab.scope">Scope</Trans>
          </Tab>
        </TabList>

        <TabPanel id={section || 'general'}>
          <Routes>
            <Route path="" element={<GeneralSection workflow={workflow} />} />
            <Route path="scope" element={<ScopeSection workflow={workflow} />} />
            <Route path="steps" element={<StepsSection workflow={workflow} />} />
          </Routes>
        </TabPanel>
      </Tabs>
    </main>
  );
};
